import { ChangeDetectionStrategy, Component, Input, OnDestroy } from "@angular/core";
import type { SafeUrl } from "@angular/platform-browser";
import { DomSanitizer } from "@angular/platform-browser";
import {
    Observable,
    ReplaySubject,
    Subject,
    UnaryFunction,
    combineLatest,
    distinctUntilChanged,
    map,
    pipe,
    startWith,
    takeUntil,
} from "rxjs";
import { CSV_DOWNLOAD_HREF_PREFIX, DEFAULT_ARIA_LABEL, DEFAULT_FILENAME, DEFAULT_VIEW_MODEL } from "../constants";
import type { CsvButtonViewModel, JsonToCSVConfig } from "../interfaces";
import { CsvService } from "../services/csv.service";
import type { DefaultData } from "../types";

type ExportCsvStream = [string, string, string, boolean | undefined];
type OperatorReturn<TReturn = unknown> = UnaryFunction<Observable<TReturn>, Observable<TReturn>>;

// @TODO: This should maybe converted into a directive for whenever we get started
//        putting a company/team design system together.
@Component({
    selector: "clare-export-csv",
    templateUrl: "./export-csv-button.component.html",
    styleUrls: ["./export-csv-button.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportCsvButtonComponent implements OnDestroy {
    public readonly vm$: Observable<CsvButtonViewModel>;

    private readonly _ariaLabel$: ReplaySubject<string>;
    private readonly _filename$: ReplaySubject<string>;
    private readonly _isDisabledOverride$: ReplaySubject<boolean>;
    private unsubscribe$ = new Subject();

    constructor(private readonly _csvService: CsvService, private readonly _sanitization: DomSanitizer) {
        this._ariaLabel$ = new ReplaySubject<string>(1);
        this._filename$ = new ReplaySubject<string>(1);
        this._isDisabledOverride$ = new ReplaySubject<boolean>(1);
        this.vm$ = combineLatest([
            this._csvService.csv$.pipe(this.commonOperators<string>("")),
            this._filename$.pipe(this.commonOperators<string>("")),
            this._ariaLabel$.pipe(this.commonOperators<string>("")),
            this._isDisabledOverride$.pipe(this.commonOperators<undefined>(undefined)),
        ]).pipe(
            map((stream: Readonly<ExportCsvStream>): CsvButtonViewModel => this.makeViewModel(stream)),
            startWith(DEFAULT_VIEW_MODEL),
            takeUntil(this.unsubscribe$)
        );
        this._ariaLabel$.next("");
        this._filename$.next("");
    }
    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }

    @Input()
    set ariaLabel(ariaLabel: string) {
        this._ariaLabel$.next(ariaLabel);
    }

    @Input()
    set columnNames(columnNames: ReadonlyArray<string>) {
        this._csvService.setColumnNames(columnNames);
    }

    @Input()
    set config(config: JsonToCSVConfig) {
        this._csvService.setConfig(config);
    }

    @Input()
    set data(data: ReadonlyArray<DefaultData>) {
        this._csvService.setJSON(data);
    }

    @Input()
    set disabled(isDisabled: boolean) {
        this._isDisabledOverride$.next(isDisabled);
    }

    @Input()
    set filename(filename: string) {
        this._filename$.next(filename);
    }

    private appendFileExtensionIfMissing(filename: string, fileExtension = "csv"): string {
        const fileExtensionPattern = new RegExp(`\\.${fileExtension}$`);
        if (fileExtensionPattern.test(filename)) {
            return filename;
        }
        return `${filename}.${fileExtension}`;
    }

    private commonOperators<TReturn = unknown>(initialValue: TReturn): OperatorReturn<TReturn> {
        return pipe(distinctUntilChanged(), startWith(initialValue));
    }

    private makeViewModel([csv, filename, ariaLabel, isDisabledOverride]: Readonly<ExportCsvStream>): CsvButtonViewModel {
        const isButtonDisabled: boolean = this.determineIfButtonIsDisabled(csv, isDisabledOverride);
        return {
            isButtonDisabled,
            ariaLabel: this.getAriaLabel(ariaLabel),
            filename: this.getFilenameForDownloadAttribute(filename, isButtonDisabled),
            hrefUrl: this.formatAndSanitizeCsvForHref(csv, isButtonDisabled),
        };
    }

    private formatAndSanitizeCsvForHref(csv: string, isButtonDisabled: boolean): SafeUrl | null {
        if (isButtonDisabled) {
            return null;
        }
        const CSV_HREF_ATTRIBUTE = `${CSV_DOWNLOAD_HREF_PREFIX}${encodeURIComponent(csv)}`;
        return this._sanitization.bypassSecurityTrustUrl(CSV_HREF_ATTRIBUTE);
    }

    private getAriaLabel(ariaLabel: string): string {
        if (ariaLabel.length === 0) {
            return DEFAULT_ARIA_LABEL;
        }
        return ariaLabel;
    }

    // eslint-disable-next-line clare-rules/prefix-methods-with-boolean-returns
    private determineIfButtonIsDisabled(csv: string, isDisabledOverride: boolean | undefined): boolean {
        if (csv.length === 0) {
            return true;
        }
        if (typeof isDisabledOverride === "boolean") {
            return isDisabledOverride;
        }
        return false;
    }

    private getFilenameForDownloadAttribute(filename: string, isButtonDisabled: boolean): string | null {
        if (isButtonDisabled) {
            return null;
        }
        if (filename.length === 0) {
            return DEFAULT_FILENAME;
        }
        return this.appendFileExtensionIfMissing(filename);
    }
}
